import {
	addDays,
	calculateDayDifference,
	calculateTimeDifference,
	getStartOfDay,
	isBetween,
	millisecondsInAMinute,
	millisecondsInAnHour,
	parseDtoDate
} from "client/DateAndTime/dateTimeUtils";
import { DateTime } from "luxon";
import { getCurrentCulture, localize } from "./culture";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { getSystemTimeZone, TimeZone } from "client/DateAndTime/timezones";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import { never } from "./errors";

export type DateFormat = "T"
	| "D"
	| "MMM"
	| "YYYY"
	| "DM"
	| "DMY"
	| "DMYT"
	| "DM(Y)"
	| "DayNameT"
	| "DayName"
	| "RelativeTimestamp"
	| "AbsoluteTimestamp"
	| "RelativeDay"
	| "RelativeDayWithTime";

// DateOnlyFormat should not have any relative or conditional based on relativity formats as that
// would require a timezone
export type DateOnlyFormat =
	| "D"
	| "MMM"
	| "YYYY"
	| "DM"
	| "DMY"
	| "DayName";

export function formatDate(
	value: Date | string,
	format: DateFormat,
	timezone: TimeZone
): string {
	return formatDateWithExpiration(
		value,
		format,
		timezone
	)
		.display;
}

// NOTE: a decision was made to make the max expiration 1 day (i.e. less accurate then in reality)
// The reasoning being that it makes the logic to compute the expiration way more straight forward.
// In addition, 1 day is infrequent enough to not cause any performance issues even if you keep your page
// loaded for this length of time
export type DisplayWithExpiration = {
	display: string;
	expiresAfterMs: number;
};

export function formatDateWithExpiration(
	value: Date | string,
	format: DateFormat,
	timezone: TimeZone
): DisplayWithExpiration {
	const parsedValue: Date = parseDtoDate(value);

	switch (format) {
		case "T":
			return formatT(parsedValue, timezone);
		case "D":
			return formatD(parsedValue, timezone);
		case "MMM":
			return formatMMM(parsedValue, timezone);
		case "YYYY":
			return formatYYYY(parsedValue, timezone);
		case "DM":
			return formatDM(parsedValue, timezone);
		case "DMY":
			return formatDMY(parsedValue, timezone);
		case "DMYT":
			return formatDMYT(parsedValue, timezone);
		case "DM(Y)":
			return formatDMOptionalY(parsedValue, timezone);
		case "DayNameT":
			return formatDayNameT(parsedValue, timezone);
		case "DayName":
			return formatDayName(parsedValue, timezone);
		case "RelativeTimestamp":
			return formatRelativeTimestamp(parsedValue, timezone);
		case "AbsoluteTimestamp":
			return formatAbsoluteTimestamp(parsedValue, timezone);
		case "RelativeDay":
			return formatRelativeDay(parsedValue, timezone);
		case "RelativeDayWithTime":
			return formatRelativeDayWithTime(parsedValue, timezone);
		default:
			never(format);
	}
}

export function formatDateOnly(
	value: DateOnly,
	format: DateOnlyFormat
): string {
	const systemTimezone: TimeZone = getSystemTimeZone(getCurrentCulture());

	const date = new Date(
		value.year,
		value.monthIndex,
		value.day
	);

	return formatDate(
		date,
		format,
		systemTimezone
	);
}

const MS_PER_DAY: number = millisecondsInAnHour * 24;

function formatT(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, DateTime.TIME_SIMPLE, timezone));
}

function formatD(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, { day: "numeric" }, timezone));
}

function formatMMM(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, { month: "short" }, timezone));
}

function formatYYYY(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, { year: "numeric" }, timezone));
}
function formatDMY(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, DateTime.DATE_MED, timezone));
}

function formatDMYT(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, DateTime.DATETIME_MED, timezone));
}

function formatDMOptionalY(value: Date, timezone: TimeZone): DisplayWithExpiration {
	const now = new Date();
	const today: DateOnly = convertToDateOnly(new Date(), timezone);
	const valueDate: DateOnly = convertToDateOnly(value, timezone);

	const isDifferentYear: boolean = today.year !== valueDate.year;

	let display: string;
	if (isDifferentYear) {
		display = formatDMY(value, timezone).display;
	} else {
		display = formatDM(value, timezone).display;
	}

	return toResult(
		display,
		getMillisecondsTillTomorrowInternal(now, timezone)
	);
}

function formatDM(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(
		formatUsingLuxon(
			value,
			{ month: "short", day: "2-digit" },
			timezone
		)
	);
}

function formatDayNameT(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(
		formatDayName(value, timezone).display + " " + formatT(value, timezone).display
	);
}

function formatDayName(value: Date, timezone: TimeZone): DisplayWithExpiration {
	return toResult(formatUsingLuxon(value, { weekday: "short" }, timezone));
}

const ONE_HOUR_IN_MINUTES: number = 60;
const ONE_DAY_IN_MINUTES: number = 60 * 24;
const FIVE_DAYS_IN_MINUTES: number = 60 * 24 * 5;

function formatRelativeTimestamp(
	value: Date,
	timezone: TimeZone
): DisplayWithExpiration {
	const now = new Date();
	const minutesDifference: number = calculateTimeDifference(
		now,
		value,
		"minute"
	);

	const nowMs: number = now.getTime();

	if (minutesDifference < 2) {
		return toResult(
			localize("_JustNow"),
			addMinutes(value, 2) - nowMs
		);
	} else if (minutesDifference < ONE_HOUR_IN_MINUTES) {
		return toResult(
			localize("_MAgo")
				.replace("{0}", minutesDifference.toString()),
			addMinutes(value, minutesDifference + 1) - nowMs
		);
	} else if (minutesDifference < ONE_DAY_IN_MINUTES) {
		const hoursDifference: number = Math.floor(minutesDifference / ONE_HOUR_IN_MINUTES);
		return toResult(
			localize("_HAgo")
				.replace("{0}", hoursDifference.toString()),
			addHours(value, hoursDifference + 1) - nowMs
		);
	} else if (minutesDifference < FIVE_DAYS_IN_MINUTES) {
		const daysDifference: number = Math.floor(minutesDifference / ONE_DAY_IN_MINUTES);
		return toResult(
			formatDayNameT(value, timezone).display,
			addDays(value, daysDifference + 1, timezone).getTime() - nowMs
		);
	} else {
		return formatDMOptionalY(value, timezone);
	}
}

function formatAbsoluteTimestamp(
	value: Date,
	timezone: TimeZone
): DisplayWithExpiration {
	const startOfCurrentDay: Date = getStartOfDay(new Date(), timezone);
	const startDayOfValue: Date = getStartOfDay(value, timezone);

	const differenceDay: number = calculateDayDifference(
		startOfCurrentDay,
		startDayOfValue,
		timezone
	);

	if (differenceDay === 0) {
		return toResult(
			formatT(value, timezone).display,
			getMillisecondsTillTomorrowInternal(startOfCurrentDay, timezone)
		);
	}

	if (differenceDay <= 5) {
		return toResult(
			formatDayNameT(value, timezone).display,
			getMillisecondsTillTomorrowInternal(startOfCurrentDay, timezone)
		);
	}

	return formatDMOptionalY(value, timezone);
}

function formatRelativeDay(value: Date, timezone: TimeZone): DisplayWithExpiration {
	const now = new Date();
	const today: Date = getStartOfDay(now, timezone);
	const yesterday: Date = addDays(today, -1, timezone);
	const tomorrow: Date = addDays(today, 1, timezone);
	const dayAfterTomorrow: Date = addDays(today, 2, timezone);

	let display: string;
	if (isBetween(value, yesterday, today)) {
		display = localize("_Yesterday");
	} else if (isBetween(value, today, tomorrow)) {
		display = localize("_Today");
	} else if (isBetween(value, tomorrow, dayAfterTomorrow)) {
		display = localize("_Tomorrow");
	} else {
		display = formatDMOptionalY(value, timezone).display;
	}

	return toResult(
		display,
		getMillisecondsTillTomorrowInternal(now, timezone)
	);
}

function formatRelativeDayWithTime(value: Date, timezone: TimeZone): DisplayWithExpiration {
	const result: DisplayWithExpiration = formatRelativeDay(value, timezone);

	result.display += " " + formatT(value, timezone).display;

	return result;
}

function toResult(
	display: string,
	expiresAfterMs: number = MS_PER_DAY
): DisplayWithExpiration {
	return {
		display,
		expiresAfterMs
	};
}

export function getMillisecondsTillTomorrow(
	timezone: TimeZone
): number {
	return getMillisecondsTillTomorrowInternal(
		new Date(),
		timezone
	);
}

function getMillisecondsTillTomorrowInternal(
	now: Date,
	timezone: TimeZone
) {
	const today: Date = getStartOfDay(now, timezone);

	const tomorrow: Date = addDays(today, 1, timezone);

	return tomorrow.getTime() - now.getTime();
}

function addMilliseconds(value: Date, amount: number): number {
	return value.getTime() + amount;
}

function addMinutes(value: Date, amount: number): number {
	return addMilliseconds(value, amount * millisecondsInAMinute);
}

function addHours(value: Date, amount: number): number {
	return addMilliseconds(value, amount * millisecondsInAnHour);
}

function formatUsingLuxon(
	date: Date,
	options: Intl.DateTimeFormatOptions,
	timezone: TimeZone
): string {
	return DateTime.fromJSDate(date, { zone: timezone.identifier })
		.toLocaleString(options);
}
